import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { SearchResultThumbnail } from '../../../../../../../common/ui/search-result-thumbnail';
import {
	enrichExcerpt,
	isAWSThumbnailUrlExpired,
	isNonPublic,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphDesign_AtlassianSearchPage$key } from './__generated__/searchGraphDesign_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphDesign = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphDesign_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchGraphDesign_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				lastModifiedDate
				permissionLevel
				entity @optIn(to: "ExternalDataDepotQueryV2") {
					... on ExternalDesign {
						__typename
						thumbnail {
							externalUrl
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, entity, permissionLevel, lastModifiedDate } = data;

	const lastModifiedComponent = lastModifiedDate && (
		<LastModified lastModified={lastModifiedDate} />
	);

	const label = formatMessage(messages.designLabel);

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const headerMetadataParts: [string, ReactNode][] = [
		['label', label],
		['lastModified', lastModifiedComponent],
		['padlock', padlockIcon],
	];

	const enrichedTitle = enrichExcerpt(title);

	const thumbnailUrl = entity?.__typename === 'ExternalDesign' && entity?.thumbnail?.externalUrl;
	const hasThumbnail = thumbnailUrl && !isAWSThumbnailUrlExpired(thumbnailUrl);
	const body = hasThumbnail ? (
		<SearchResultThumbnail resultUrl={url} thumbnailUrl={thumbnailUrl} />
	) : null;

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			{...data}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			description={body}
			headerMetadataParts={headerMetadataParts}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			icon={icon}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
